import React, { useMemo, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import Paper from "@material-ui/core/Paper";
import { IconButton, Tooltip } from "@mui/material"
import { useTranslation } from "react-i18next";
import { Dialog } from "@material-ui/core";
import { Alert } from "@mui/material";
import { Snackbar } from "@material-ui/core";
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
    tableStyle: {
        border: "none",
        boxShadow: "none",
    },
    tableText: {
        fontFamily: "Open Sans",
        fontWeight: 'normal',
        fontSize: "12px",
        lineHeight: "16px",
        color: "#141414",
        textAlign: "center",
        padding: "6px",
        minWidth: "120px",
        verticalAlign: "middle"
    },
    tableHeader: {
        textAlign: "center",
        fontFamily: "Open Sans",
        fontStyle: "normal",
        fontWeight: "bold",
        color: '#6F6F6F',
        fontSize: "14px",
        lineHeight: "19px",
        padding: "6px",
    },
    actionCell: {
        padding: "6px",
        verticalAlign: "middle", 
    },
    flexContainer: {
        display: "flex", 
        flexDirection: "row", 
        justifyContent: "space-between", 
        alignItems: "center"
    },
    odd: {
        background: "#F3F3F3",
    },
    even: {
        background: "#FFFFFF",
    }
}));

const getPresignedUrl = async (documentUrlData, user_data, document_analysis_id, disposition) => {
    if (isUrlCached(documentUrlData)) {
        const url = new URL(documentUrlData.url)
        const cached_disposition = url.searchParams.get('response-content-disposition');
        if(cached_disposition === disposition){
            return url.href;
        }
    }
    const dashAPIEndpointUrl = `/dash/document_analysis/document/${document_analysis_id}/presigned_url`
    const queryParameters = {"content_disposition": disposition}
    try {
        const headers = (user_data?.business_group)? {"COMPANY-KEY": user_data.company_key} : {}
        const response = await axios.get(dashAPIEndpointUrl, {headers: headers, params: queryParameters})

        const presigned_url = response.data.presigned_url 
        return presigned_url
    }
    catch (error){
        throw error
    }
}

const isUrlCached = (documentUrlData) => {
    const isUrlValid = url => url !== "about:blank"
    const timestamp = documentUrlData.timestamp || (Date.now() - 3600000)
    const now = Date.now()
    const ageInMilliseconds = now - timestamp;
    const thirtyMinutes = 30 * 60 * 1000;
    return (isUrlValid(documentUrlData) && ageInMilliseconds < thirtyMinutes) 
}

const getFilename = (url) => {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    return pathname.substring(pathname.lastIndexOf('/') + 1)
}

function DownloadDocumentButton(props) {
    const { t } = useTranslation();
    const [isDisabled, setIsDisabled] = useState(false)
    const { documentUrlData, setDocumentUrlData } = props
    const { user_data } = props
    const { document_analysis_id } = props
    const { setErrorSnackbarData } = props
    const buttonText = t("Baixar Documento");

    async function DownloadDocument(){
        setIsDisabled(true)
        try {
            const url = await getPresignedUrl(documentUrlData, user_data, document_analysis_id, 'attachment')
            const response = await fetch(url,{ mode: "cors"})
            setDocumentUrlData({"url": url, "timestamp": Date.now()})
            
            const resolvedFilename = getFilename(url)
            const BLOBUrl = window.URL.createObjectURL(await response.blob())
            const link = document.createElement('a')
            link.href = BLOBUrl
            link.setAttribute('download',  resolvedFilename)
            document.body.appendChild(link)
            link.click()

            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(BLOBUrl);
            }, 100);
        }
        catch (error){
            setIsDisabled(false)
            console.error(`Something went wrong requesting the document: ${error}`)
            setErrorSnackbarData({open: true, message: t("Não foi possível baixar o documento, tente novamente")})
            throw error 
        }
        setIsDisabled(false)
    }

    return (
        <Tooltip title={buttonText} disableInteractive>
            <IconButton aria-label={buttonText} onClick={DownloadDocument} style={{padding: 0}} disabled={isDisabled}>
                <GetAppIcon/>
            </IconButton>
        </Tooltip>
    );
}

function ViewDocumentButton(props) {
    const { t } = useTranslation();
    const [isDisabled, setIsDisabled] = useState(false)
    const [ openDocumentView, setOpenDocumentView] = useState(false)
    const [ viewDocumentUrl, setViewDocumentUrl ] = useState('')
    const [ shouldUseIframe, setShouldUseIframe ] = useState(false)
    const { user_data } = props
    const { document_analysis_id } = props
    const { setErrorSnackbarData } = props
    const { documentUrlData, setDocumentUrlData } = props
    const buttonText = t("Visualizar Documento");

    const handleDocumentViewClose = () => {
        setOpenDocumentView(false)
    }

    async function viewDocument(){
        setIsDisabled(true)
        try {
            const url = await getPresignedUrl(documentUrlData, user_data, document_analysis_id, 'inline')
            const resolvedFilename = getFilename(url)
            const [, ...fileExtensions] = resolvedFilename.split('.')
            const fileType = fileExtensions.join('.')
            setShouldUseIframe(()=> {
                const iframeExtensions = ['pdf']
                return iframeExtensions.includes(fileType)
            })
            setDocumentUrlData({"url": url, "timestamp": Date.now()})
            setViewDocumentUrl(url)
            setOpenDocumentView(true)
        }
        catch (error){
            setIsDisabled(false)
            console.error(`Something went wrong requesting the document: ${error}`)
            setErrorSnackbarData({open: true, message: t("Não foi possível visualizar o documento, tente novamente")})
            throw error
        }
        setIsDisabled(false)
    }

    const handleDocumentLoad = () => {
        window.URL.revokeObjectURL(viewDocumentUrl)
    }

    return (
        <>
            <Tooltip title={buttonText} disableInteractive>
                <IconButton onClick={viewDocument} aria-label={buttonText} style={{padding: 0}} disabled={isDisabled}>
                    <OpenInFullRoundedIcon/>
                </IconButton>
            </Tooltip>
            <Dialog 
                open={openDocumentView}
                onClose={handleDocumentViewClose}
                fullWidth
                maxWidth="lg"
                PaperProps={{
                    style: {
                        padding: "20px 25px",
                        height: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={handleDocumentViewClose}
                    sx={{
                        position: 'absolute',
                        padding: 0,
                        right: 1,
                        top: 2,
                    }}
                    size="large"
                >
                    <CloseIcon />
                </IconButton>
                {shouldUseIframe? 
                <iframe
                    src={viewDocumentUrl+"#view=fitH"}
                    onLoad={handleDocumentLoad}
                    alt="Document"
                    title="Document"
                    style={{
                        flex: 1,
                        width: '100%',
                        minHeight: 0,
                        border: 'none'
                    }}
                />
                :
                <img 
                    src={viewDocumentUrl}
                    onLoad={handleDocumentLoad}
                    alt="Document"
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        flex: 1,
                        minHeight: 0
                    }}
                />
                }
        </Dialog>
        </>
    );
}

function AnalysedDocumentRow(props){
    const { t } = useTranslation()
    const classes = useStyles()
    useTheme()

    const [documentUrlData, setDocumentUrlData] = useState({url: "about:blank", timestamp: null})
    const { index } = props
    const { analysis } = props

    const getParityClass = (rowIndex) => {
        if (rowIndex % 2 === 0) return classes.even
        else return classes.odd;
    }         

    return (
        <TableRow className={getParityClass(index)}>
            <TableCell className={classes.tableText}>{analysis.document_analysis_id}</TableCell>
            <TableCell className={classes.tableText}>{t(analysis.type)}</TableCell>
            <TableCell
                className={classes.actionCell}
            >
                <div className={classes.flexContainer}>
                    <DownloadDocumentButton 
                        document_analysis_id={analysis.document_analysis_id}
                        documentUrlData={documentUrlData} 
                        setDocumentUrlData={setDocumentUrlData}
                        setErrorSnackbarData={props.setErrorSnackbarData}
                        user_data={props.user_data}
                    />
                    <ViewDocumentButton 
                        document_analysis_id={analysis.document_analysis_id}
                        documentUrlData={documentUrlData} 
                        setDocumentUrlData={setDocumentUrlData}
                        setErrorSnackbarData={props.setErrorSnackbarData}
                        user_data={props.user_data}
                    />
                </div>
            </TableCell>
        </TableRow>
    )
}

export default function AnalysedDocumentsTable(props) {
    const { t } = useTranslation()
    const classes = useStyles();
    useTheme();

    const { documents } = props
    const [ errorSnackbarData, setErrorSnackbarData ] = useState({open: false, message: ""})

    const analyses = useMemo(()=> {
        const analysed_documents = Object.entries(documents)
            .filter(([_, value]) =>
                Object.entries(value)
                    .some(([key, _]) =>
                        ["document_analysis_ids", "document_analysis_id"].includes(key)
                    )
            )

        return analysed_documents.flatMap(([key, value]) => {
                value = value?.["document_analysis_ids"] ?? value?.["document_analysis_id"]

                const ids = Array.isArray(value)? value : [value]

                return ids.map(id => ({ document_analysis_id: id, type: key }));
            })
    }, [documents])

    return ( 
    <>
        <Paper className={classes.tableStyle}>
        <Table style={{ padding: "8px" }}>
            <TableHead className={classes.odd}>
            <TableRow>
                <TableCell className={classes.tableHeader}>{t("ID do Documento")}</TableCell>
                <TableCell className={classes.tableHeader}>{t("Tipo do Documento")}</TableCell>
                <TableCell style={{width: "10%"}} className={classes.tableHeader}>{t("Ações")}</TableCell>
            </TableRow>
            </TableHead>
            <TableBody>
                {analyses.map((analysis, index) => 
                    <AnalysedDocumentRow 
                        analysis={analysis}
                        index={index}
                        setErrorSnackbarData={setErrorSnackbarData}
                        user_data={props.user_data}
                    />
                )}
            </TableBody>
        </Table>
        <Snackbar
            open={errorSnackbarData.open}
            autoHideDuration={5000}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={() => setErrorSnackbarData((prev) => ({...prev, open:false}))}
        >
            <Alert variant="filled" severity="error">
                {errorSnackbarData.message}
            </Alert>
        </Snackbar>
        </Paper>
    </>
  );
}
